'use client';

/**
 * UnitBookingClient Component
 *
 * Client-side booking functionality for units from Firestore.
 * Similar to LodgeBookingClient but adapted for the Unit type.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useSafeTranslations } from '@/hooks/useSafeTranslations';
import { format } from 'date-fns';
import type { Unit } from '@/lib/admin/units-service';
import BookingCalendar from '@/components/booking/BookingCalendar';
import BookingCart, { CartItem } from '@/components/booking/BookingCart';
import ActivityCrossSell from '@/components/booking/ActivityCrossSell';
import ServicesCrossSell from '@/components/booking/ServicesCrossSell';
import ServiceRecommendation from '@/components/booking/services/ServiceRecommendation';
import { DualPrice } from '@/components/pricing/DualPrice';
import { useExchangeRate } from '@/lib/hooks/useExchangeRate';
import { useUnifiedCart } from '@/lib/unified-cart';
import { getSessionId } from '@/lib/session';
import styles from './LodgeBookingClient.module.css';

/**
 * Direct quote response from /api/quotes/direct
 */
interface DirectQuoteResponse {
  success: boolean;
  data?: {
    quoteId: string;
    ratePlanId: string;
    expiresAt: string;
    pricing: {
      accommodation: number;
      cleaning: number;
      fees: number;
      taxes: number;
      total: number;
      currency: string;
    };
    // Backend returns checkIn/checkOut as flat fields, not nested in dates
    checkIn: string;
    checkOut: string;
    listingId: string;
    guests: number;
    pricePerNight?: number;
    dailyPrices?: Array<{
      date: string;
      price: number;
    }>;
    nightlyRates?: Record<string, number>;
    raw?: unknown;
  };
  error?: {
    code?: string;
    message?: string;
    details?: {
      nightlyRates?: Record<string, number>;
      cleaningFee?: number;
      estimatedTotal?: number;
    };
  } | string;
}

/** Debounce delay for price fetching (ms) */
const PRICE_FETCH_DEBOUNCE_MS = 400;

interface UnitBookingClientProps {
  unit: Unit;
  /** Forwarded to BookingCalendar; keeps parent hero stat/banner in sync. */
  onEffectiveMinNightsChange?: (n: number) => void;
  /**
   * Optional prefill (e.g. from `?checkIn=&checkOut=&guests=` query params, already
   * validated by the parent). Only consumed on the FIRST render — the user can freely
   * change dates/guests afterwards without them snapping back.
   */
  initialBooking?: { checkIn: string; checkOut: string; guests?: number };
}

/**
 * UnitBookingClient component
 */
export default function UnitBookingClient({ unit, onEffectiveMinNightsChange, initialBooking }: UnitBookingClientProps) {
  const router = useRouter();
  const t = useSafeTranslations('bookingLodge');
  const rawLocale = useLocale();
  const calendarLocale = (['en', 'es', 'pt'].includes(rawLocale) ? rawLocale : 'en') as 'en' | 'es' | 'pt';
  const {
    addLodgeItem, removeItem, lodgeItems, activityItems,
    lodgeTotalCLP, activitySubtotalUSD, activityServiceFee, activityTotalUSD,
    grandTotalUSD, exchangeRate: cartExchangeRate, couponDiscount,
  } = useUnifiedCart();

  // Determine if we should use Guesty BE API
  const useGuestyAPI = Boolean(unit.guestyListingId);

  // Prefill range, computed ONCE (empty deps on purpose): only seeds the initial
  // state below, never re-derived, so later user edits are never clobbered.
  const initialRange = useMemo(() => {
    if (!initialBooking) return undefined;
    return {
      start: new Date(`${initialBooking.checkIn}T12:00:00`),
      end: new Date(`${initialBooking.checkOut}T12:00:00`),
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // State
  const [checkIn, setCheckIn] = useState<Date | null>(initialRange?.start ?? null);
  const [checkOut, setCheckOut] = useState<Date | null>(initialRange?.end ?? null);
  const [guests, setGuests] = useState<number>(() => {
    // No-prefill path stays byte-identical to the original default.
    if (initialBooking?.guests === undefined) return unit.baseOccupancy || 2;
    return Math.max(1, Math.min(unit.maxOccupancy, initialBooking.guests));
  });
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [minimumNightsRequired, setMinimumNightsRequired] = useState<number>(unit.pricing.minimumNights || 1);
  const [meetsMinimumNights] = useState<boolean>(true);
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [pricePerNight, setPricePerNight] = useState<number | null>(null);

  // Direct quote data (replaces useQuote hook)
  const [currentQuote, setCurrentQuote] = useState<DirectQuoteResponse['data'] | null>(null);
  const [quoteExpired, setQuoteExpired] = useState<boolean>(false);
  const [minutesUntilExpiry, setMinutesUntilExpiry] = useState<number>(0);

  // Primary pricing from Guesty nightlyRates (used when official quote unavailable)
  const [isEstimatedPrice, setIsEstimatedPrice] = useState<boolean>(false);
  const [nightlyRates, setNightlyRates] = useState<Record<string, number> | null>(null);
  const [nightlyCleaningFee, setNightlyCleaningFee] = useState<number>(0);

  // Feature flag: set to false to revert to original spinner behavior
  const INSTANT_CACHE_PRICING = true;
  // Track if current price comes from calendar cache
  const [pricedFromCache, setPricedFromCache] = useState(false);
  // Track background quote fetch (skeleton pulse on price values)
  const [backgroundQuoteLoading, setBackgroundQuoteLoading] = useState(false);
  // Defense-in-depth: price must be verified by Guesty API before cart add (when GST active)
  const [guestyPriceVerified, setGuestyPriceVerified] = useState(false);

  // Get guest capacity from unit
  const maxGuests = unit.maxOccupancy;
  const minimumNights = minimumNightsRequired;

  // Calculate nights
  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  }, [checkIn, checkOut]);

  // Exchange rate for consistent USD total (avoids rounding discrepancy between line items and total)
  const { convertCLPtoUSD, loading: rateLoading } = useExchangeRate();
  const isCLP = (unit.pricing.basePrice.currency || 'CLP') === 'CLP';

  // Compute USD total as sum of individually-rounded line items so displayed total = sum of displayed parts
  const consistentUSDTotal = useMemo(() => {
    if (!isCLP || !pricePerNight || !nights) return null;
    const perNightUSD = convertCLPtoUSD(pricePerNight);
    const cleaningUSD = convertCLPtoUSD(nightlyCleaningFee);
    return Math.round((perNightUSD * nights + cleaningUSD) * 100) / 100;
  }, [isCLP, pricePerNight, nights, nightlyCleaningFee, convertCLPtoUSD]);

  // Convert unified cart items (lodge + activity) to BookingCart's CartItem format (single source of truth)
  const cartItemsFromContext = useMemo((): CartItem[] => {
    const lodgeCartItems: CartItem[] = lodgeItems.map(item => ({
      cartItemId: item.cartItemId,
      type: 'lodge' as const,
      id: item.itemId,
      name: item.itemName,
      startDate: new Date(item.checkIn + 'T12:00:00'),
      endDate: new Date(item.checkOut + 'T12:00:00'),
      guests: item.guests,
      imageUrl: item.itemImage,
      priceBreakdown: item.priceBreakdown as CartItem['priceBreakdown'],
      priceCLP: item.priceCLP,
      loadingPrice: false,
      quoteId: item.quoteId,
      ratePlanId: item.ratePlanId,
      guestyPricing: item.guestyPricing as CartItem['guestyPricing'],
    }));

    const activityCartItems: CartItem[] = activityItems.map(item => ({
      cartItemId: item.cartItemId,
      type: 'activity' as const,
      id: item.activityId,
      name: item.activityName,
      startDate: new Date(item.date + 'T12:00:00'),
      endDate: new Date(item.date + 'T12:00:00'),
      guests: item.persons,
      imageUrl: item.activityImage,
      priceBreakdown: {
        basePrice: item.subtotalUSD,
        extraGuestFees: 0,
        cleaningFee: 0,
        subtotal: item.subtotalUSD,
        promoDiscount: 0,
        groupDiscount: 0,
        totalDiscounts: 0,
        total: item.subtotalUSD,
        currency: 'USD' as const,
        pricePerPerson: item.pricePerPerson,
      },
      loadingPrice: false,
    }));

    return [...lodgeCartItems, ...activityCartItems];
  }, [lodgeItems, activityItems]);

  /**
   * Handle date range selection
   */
  const handleDateSelect = useCallback((range: {
    start: Date;
    end: Date;
    cachedNightlyRates?: Record<string, number>;
    cacheComplete?: boolean;
  } | null) => {
    if (range) {
      setCheckIn(range.start);
      setCheckOut(range.end);
      setPriceError(null);

      if (useGuestyAPI && INSTANT_CACHE_PRICING && range.cacheComplete && range.cachedNightlyRates) {
        // GST + cache available: show layout with skeleton pulse (UX) but
        // keep estimatedPrice=null so it can NEVER be added to cart.
        // Only Guesty API can populate estimatedPrice (security).
        const ratesArray = Object.values(range.cachedNightlyRates);
        const nightlyTotal = ratesArray.reduce((sum, r) => sum + r, 0);
        const cleaningFee = unit.pricing.cleaningFee?.amount || 0;
        const avgPerNight = ratesArray.length > 0
          ? Math.round(nightlyTotal / ratesArray.length) : 0;

        // Visual-only preview (skeleton pulse will cover the values)
        setEstimatedPrice(nightlyTotal + cleaningFee);
        setPricePerNight(avgPerNight);
        setNightlyRates(range.cachedNightlyRates);
        setNightlyCleaningFee(cleaningFee);
        setIsEstimatedPrice(true);
        setCurrentQuote(null);
        setPriceLoading(false);
        setPricedFromCache(true);
        // Block cart + show skeleton on all values
        setBackgroundQuoteLoading(true);
        setGuestyPriceVerified(false);
      } else if (useGuestyAPI) {
        // GST but no cache: full spinner
        setEstimatedPrice(null);
        setPricePerNight(null);
        setCurrentQuote(null);
        setIsEstimatedPrice(false);
        setNightlyRates(null);
        setNightlyCleaningFee(0);
        setPricedFromCache(false);
        setBackgroundQuoteLoading(false);
        setPriceLoading(true);
        setGuestyPriceVerified(false);
      } else if (INSTANT_CACHE_PRICING && range.cacheComplete && range.cachedNightlyRates) {
        // Non-GST: instant price from calendar cache
        const ratesArray = Object.values(range.cachedNightlyRates);
        const nightlyTotal = ratesArray.reduce((sum, r) => sum + r, 0);
        const cleaningFee = unit.pricing.cleaningFee?.amount || 0;
        const avgPerNight = ratesArray.length > 0
          ? Math.round(nightlyTotal / ratesArray.length) : 0;

        setEstimatedPrice(nightlyTotal + cleaningFee);
        setPricePerNight(avgPerNight);
        setNightlyRates(range.cachedNightlyRates);
        setNightlyCleaningFee(cleaningFee);
        setIsEstimatedPrice(true);
        setCurrentQuote(null);
        setPriceLoading(false);
        setPricedFromCache(true);
      }
      // If cache incomplete → useEffect will show spinner
    } else {
      setCheckIn(null);
      setCheckOut(null);
      setEstimatedPrice(null);
      setPricePerNight(null);
      setPricedFromCache(false);
      setBackgroundQuoteLoading(false);
      setGuestyPriceVerified(false);
    }
  }, [unit.pricing.cleaningFee?.amount, useGuestyAPI]);

  /**
   * Handle guest count change
   */
  const handleGuestChange = useCallback((newGuests: number) => {
    setGuests(Math.max(1, Math.min(maxGuests, newGuests)));
  }, [maxGuests]);

  // Ref to track abort controller for cancelling stale requests
  const abortControllerRef = useRef<AbortController | null>(null);
  // Ref to track debounce timeout
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Fetch price breakdown when dates/guests change
   * Uses direct Guesty BE API endpoint with Firestore token
   *
   * IMPORTANT: Uses debouncing + AbortController to prevent request flooding
   */
  useEffect(() => {
    // Clear any pending debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // No dates selected - reset state immediately (no debounce needed)
    if (!checkIn || !checkOut) {
      setEstimatedPrice(null);
      setPricePerNight(null);
      setCurrentQuote(null);
      setIsEstimatedPrice(false);
      setNightlyRates(null);
      setPriceLoading(false);
      setPricedFromCache(false);
      setBackgroundQuoteLoading(false);
      return;
    }

    // Cache already has the price → no spinner, background quote only
    const hasExtraGuestFees = guests > (unit.baseOccupancy || 2) &&
      (unit.pricing.extraGuestPrice?.amount || 0) > 0;

    if (INSTANT_CACHE_PRICING && pricedFromCache && estimatedPrice !== null && !hasExtraGuestFees) {
      if (!useGuestyAPI) return;

      // Silent background quote (skeleton pulse on price values, no full spinner)
      setBackgroundQuoteLoading(true);
      debounceTimeoutRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        try {
          const checkInStr = checkIn.toISOString().split('T')[0];
          const checkOutStr = checkOut.toISOString().split('T')[0];
          const apiBase = process.env.NEXT_PUBLIC_API_URL || '';
          const apiUrl = apiBase ? `${apiBase}/api/quotes/direct` : '/api/quotes/direct';

          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ checkIn: checkInStr, checkOut: checkOutStr, guests, listingId: unit.guestyListingId }),
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          const data: DirectQuoteResponse = await response.json();
          if (controller.signal.aborted) return;

          // Only update if official quote succeeds (rare ~1%)
          if (data.success && data.data?.pricing?.total != null) {
            setCurrentQuote(data.data);
            setEstimatedPrice(data.data.pricing.total);
            // Derive pricePerNight from total to guarantee visual consistency
            const bgCleaningFee = data.data.pricing.cleaning || 0;
            const bgAccommodation = data.data.pricing.total - bgCleaningFee;
            const bgNights = Math.ceil(
              (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
            );
            setPricePerNight(
              data.data.pricePerNight ??
              (bgNights > 0 ? Math.round(bgAccommodation / bgNights) : pricePerNight)
            );
            if (data.data.nightlyRates && Object.keys(data.data.nightlyRates).length > 0) {
              setNightlyRates(data.data.nightlyRates);
            }
            setNightlyCleaningFee(bgCleaningFee);
            setIsEstimatedPrice(false);
            const expiresAt = new Date(data.data.expiresAt);
            const minutesLeft = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 60000));
            setMinutesUntilExpiry(minutesLeft);
            setQuoteExpired(minutesLeft === 0);
            setGuestyPriceVerified(true);
          }
          // Fallo con nightlyRates de Guesty Open API (99%): actualizar precio
          else if (!data.success && typeof data.error === 'object' && data.error?.details?.nightlyRates) {
            const nr = data.error.details.nightlyRates;
            const nrTotal = Object.values(nr).reduce((s, r) => s + r, 0);
            if (nrTotal > 0) {
              const apiCF = data.error.details.cleaningFee || 0;
              const fsCF = unit.pricing.cleaningFee?.amount || 0;
              const cf = apiCF > 0 ? apiCF : fsCF;
              const total = data.error.details.estimatedTotal || (nrTotal + cf);
              const rates = Object.values(nr);
              const ppn = rates.length > 0 ? Math.round(nrTotal / rates.length) : 0;

              setEstimatedPrice(total);
              setPricePerNight(ppn);
              setNightlyRates(nr);
              setNightlyCleaningFee(cf);
              setIsEstimatedPrice(true);
              setCurrentQuote(null);
              setGuestyPriceVerified(true);
            }
          }
          // Property disabled in Guesty: clear cached price, show unavailability
          else if (!data.success && typeof data.error === 'object' && data.error?.code === 'LISTING_NOT_AVAILABLE') {
            setEstimatedPrice(null);
            setPricePerNight(null);
            setCurrentQuote(null);
            setIsEstimatedPrice(false);
            setNightlyRates(null);
            setNightlyCleaningFee(0);
            setPricedFromCache(false);
            setPriceError('This property is not currently available for booking.');
            setGuestyPriceVerified(false);
          }
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') return;
        } finally {
          if (!controller.signal.aborted) {
            setBackgroundQuoteLoading(false);
          }
        }
      }, PRICE_FETCH_DEBOUNCE_MS);

      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      };
    }

    // Set loading state immediately for UI feedback
    setPriceLoading(true);

    // Debounce the actual API call
    debounceTimeoutRef.current = setTimeout(async () => {
      // Create new abort controller for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setPriceError(null);

      try {
        const checkInStr = checkIn.toISOString().split('T')[0];
        const checkOutStr = checkOut.toISOString().split('T')[0];

        // Check if aborted before making request
        if (controller.signal.aborted) return;

        // Call direct quote endpoint (reads token from Firestore automatically)
        // Use Cloud Functions URL if available, fallback to local API route
        const apiBase = process.env.NEXT_PUBLIC_API_URL || '';
        const apiUrl = apiBase ? `${apiBase}/api/quotes/direct` : '/api/quotes/direct';

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            checkIn: checkInStr,
            checkOut: checkOutStr,
            guests,
            listingId: unit.guestyListingId
          }),
          signal: controller.signal,
        });

        // Check if aborted
        if (controller.signal.aborted) return;

        const data: DirectQuoteResponse = await response.json();

        // Check again after parsing
        if (controller.signal.aborted) return;

        // Validate response has required nested data structure
        const hasValidQuoteData = data.success &&
          data.data &&
          data.data.pricing &&
          data.data.checkIn &&
          data.data.checkOut &&
          typeof data.data.pricing.total === 'number';

        if (!response.ok || !hasValidQuoteData) {
          // Check if we have nightlyRates pricing from Guesty availability check
          const errorObj = typeof data.error === 'object' ? data.error : null;
          const nightlyRatesData = errorObj?.details?.nightlyRates;
          const apiEstimatedTotal = errorObj?.details?.estimatedTotal;

          // Get cleaningFee: first from API, then from Firestore unit data
          const apiCleaningFee = errorObj?.details?.cleaningFee || 0;
          const firestoreCleaningFee = unit.pricing.cleaningFee?.amount || 0;
          const cleaningFee = apiCleaningFee > 0 ? apiCleaningFee : firestoreCleaningFee;

          // Calculate total: if API total doesn't include cleaning, add Firestore cleaningFee
          const nightlyTotal = nightlyRatesData
            ? Object.values(nightlyRatesData).reduce((sum, rate) => sum + rate, 0)
            : 0;
          const totalWithCleaning = (apiCleaningFee > 0 && apiEstimatedTotal)
            ? apiEstimatedTotal
            : nightlyTotal + cleaningFee;

          if (nightlyRatesData && nightlyTotal > 0) {
            // Use primary pricing from Guesty nightlyRates + Firestore cleaningFee
            console.log('[UnitBookingClient] Using Guesty nightlyRates pricing:', {
              nightlyTotal,
              cleaningFee,
              cleaningFeeSource: apiCleaningFee > 0 ? 'API' : 'Firestore',
              total: totalWithCleaning,
              nights: Object.keys(nightlyRatesData).length
            });

            // Calculate price per night from nightlyRates
            const ratesArray = Object.values(nightlyRatesData);
            const avgPricePerNight = ratesArray.length > 0
              ? Math.round(ratesArray.reduce((sum, rate) => sum + rate, 0) / ratesArray.length)
              : 0;

            setEstimatedPrice(totalWithCleaning);
            setPricePerNight(avgPricePerNight);
            setNightlyRates(nightlyRatesData);
            setNightlyCleaningFee(cleaningFee);
            setIsEstimatedPrice(true);
            setCurrentQuote(null);
            setQuoteExpired(false);
            setMinutesUntilExpiry(0);
            setPriceError(null);
            setPriceLoading(false);
            setGuestyPriceVerified(true);
            return;
          }

          // Complete failure - no pricing available
          const errorMsg = errorObj?.code === 'LISTING_NOT_AVAILABLE'
            ? 'This property is not currently available for booking.'
            : (errorObj?.message || (typeof data.error === 'string' ? data.error : `Failed to fetch pricing: ${response.status}`));
          console.error('[UnitBookingClient] Quote error:', errorMsg);
          setPriceError(errorMsg);
          setEstimatedPrice(null);
          setPricePerNight(null);
          setCurrentQuote(null);
          setIsEstimatedPrice(false);
          setNightlyRates(null);
          setPriceLoading(false);
          return;
        }

        // Success - update state with official quote data
        // TypeScript narrowing: hasValidQuoteData ensures data.data exists
        const quoteData = data.data!;
        setCurrentQuote(quoteData);
        setEstimatedPrice(quoteData.pricing.total);

        // Calculate pricePerNight from nightlyRates if backend doesn't provide it
        const nightlyRatesFromQuote = quoteData.nightlyRates || {};
        const ratesArray = Object.values(nightlyRatesFromQuote);
        const calculatedPricePerNight = ratesArray.length > 0
          ? Math.round(ratesArray.reduce((sum: number, rate) => sum + rate, 0) / ratesArray.length)
          : null;
        // Fallback: derive from total to guarantee visual consistency
        const derivedFromTotal = nights > 0
          ? Math.round((quoteData.pricing.total - (quoteData.pricing.cleaning || 0)) / nights)
          : null;

        setPricePerNight(quoteData.pricePerNight ?? calculatedPricePerNight ?? derivedFromTotal);
        setNightlyRates(quoteData.nightlyRates || null);
        setNightlyCleaningFee(quoteData.pricing.cleaning || 0);
        setIsEstimatedPrice(false); // Official quote, not estimated
        setPriceError(null);

        // Calculate minutes until expiry
        const expiresAt = new Date(quoteData.expiresAt);
        const now = new Date();
        const minutesLeft = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 60000));
        setMinutesUntilExpiry(minutesLeft);
        setQuoteExpired(minutesLeft === 0);

        setGuestyPriceVerified(true);

        console.log('[UnitBookingClient] Quote successful:', {
          quoteId: quoteData.quoteId,
          total: quoteData.pricing.total,
          nights: quoteData.nightlyRates ? Object.keys(quoteData.nightlyRates).length : 0,
          expiresIn: `${minutesLeft}min`
        });

      } catch (error) {
        // Don't log or set error for aborted requests
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('[UnitBookingClient] Price fetch error:', error);
        setPriceError('Unable to calculate price. Please try again.');
        setEstimatedPrice(null);
        setPricePerNight(null);
        setCurrentQuote(null);
        setIsEstimatedPrice(false);
        setNightlyRates(null);
      } finally {
        // Only update loading state if this request wasn't aborted
        if (!controller.signal.aborted) {
          setPriceLoading(false);
        }
      }
    }, PRICE_FETCH_DEBOUNCE_MS);

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [checkIn, checkOut, guests, unit.id, unit.guestyListingId]);

  /**
   * Add to cart — writes ONLY to UnifiedCartContext (single source of truth)
   */
  const handleAddToCart = useCallback(async () => {
    if (!checkIn || !checkOut) return;
    // Defense-in-depth: reject unverified prices when GST is active (prevents DevTools bypass)
    if (useGuestyAPI && !guestyPriceVerified) {
      console.warn('[UnitBookingClient] Blocked add-to-cart: Guesty price not verified');
      return;
    }

    const heroImage = unit.images?.[0] || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80';

    try {
      let priceBreakdown;

      if (useGuestyAPI && currentQuote) {
        const quoteCurrency = (currentQuote.pricing.currency || 'CLP') as 'USD' | 'CLP' | 'BRL' | 'EUR';
        priceBreakdown = {
          basePrice: currentQuote.pricing.accommodation,
          extraGuestFees: 0,
          cleaningFee: currentQuote.pricing.cleaning,
          subtotal: currentQuote.pricing.accommodation + currentQuote.pricing.cleaning,
          promoDiscount: 0,
          groupDiscount: 0,
          totalDiscounts: 0,
          total: currentQuote.pricing.total,
          currency: quoteCurrency,
          // currentQuote.pricePerNight can be undefined (raw quote field); fall back
          // to the robustly-derived state value, then to accommodation/nights, so the
          // cart breakdown always renders the per-night row. Average = accommodation/nights.
          pricePerNight:
            currentQuote.pricePerNight ??
            pricePerNight ??
            (nights > 0 ? Math.round(currentQuote.pricing.accommodation / nights) : 0),
          nights,
        };
      } else if (useGuestyAPI && isEstimatedPrice && nightlyRates && estimatedPrice) {
        const ratesArray = Object.values(nightlyRates);
        const accommodationTotal = ratesArray.reduce((sum, rate) => sum + rate, 0);
        const avgPricePerNight = ratesArray.length > 0
          ? Math.round(accommodationTotal / ratesArray.length)
          : 0;
        const currency = (unit.pricing.basePrice.currency || 'CLP') as 'USD' | 'CLP' | 'BRL' | 'EUR';
        priceBreakdown = {
          basePrice: accommodationTotal,
          extraGuestFees: 0,
          cleaningFee: nightlyCleaningFee,
          subtotal: accommodationTotal + nightlyCleaningFee,
          promoDiscount: 0,
          groupDiscount: 0,
          totalDiscounts: 0,
          total: estimatedPrice,
          currency,
          pricePerNight: avgPricePerNight,
          nights,
        };
      } else {
        const params = new URLSearchParams({
          startDate: checkIn.toISOString().split('T')[0],
          endDate: checkOut.toISOString().split('T')[0],
          guests: guests.toString(),
          // Pulsera de sesión: que el propio hold del cliente no le bloquee el
          // precio de SUS fechas. Ver lib/session.ts.
          sessionId: getSessionId(),
        });
        const apiBase = process.env.NEXT_PUBLIC_API_URL;
        const apiUrl = apiBase ? `${apiBase}/api` : '/api';
        const response = await fetch(`${apiUrl}/availability/lodge/${unit.id}?${params}`);
        if (!response.ok) throw new Error('Failed to fetch pricing');
        const responseData = await response.json();
        const pricingData = responseData?.data?.pricing;
        if (!pricingData) throw new Error('Pricing not available for selected dates');
        priceBreakdown = {
          basePrice: pricingData.breakdown?.basePrice || 0,
          extraGuestFees: pricingData.breakdown?.extraGuestFees || 0,
          cleaningFee: pricingData.breakdown?.cleaningFee || 0,
          subtotal: pricingData.breakdown?.subtotal || pricingData.total || 0,
          promoDiscount: pricingData.breakdown?.promoDiscount || 0,
          groupDiscount: pricingData.breakdown?.groupDiscount || 0,
          totalDiscounts: pricingData.breakdown?.totalDiscounts || 0,
          total: pricingData.total || 0,
          currency: pricingData.currency || 'USD',
          pricePerNight: pricingData.pricePerNight,
          nights,
        };
      }

      const checkInStr = checkIn.toISOString().split('T')[0];
      const checkOutStr = checkOut.toISOString().split('T')[0];

      addLodgeItem({
        itemType: 'lodge',
        itemId: unit.id,
        itemSlug: unit.id,
        itemName: unit.name,
        itemImage: heroImage,
        locationId: unit.locationId,
        checkIn: checkInStr,
        checkOut: checkOutStr,
        nights,
        guests,
        priceCLP: priceBreakdown.total,
        cleaningFeeCLP: priceBreakdown.cleaningFee || 0,
        nightlyRates: nightlyRates || {},
        priceBreakdown: priceBreakdown,
        ...(currentQuote && { quoteId: currentQuote.quoteId }),
        ...(currentQuote && { ratePlanId: currentQuote.ratePlanId }),
        ...(currentQuote && {
          guestyPricing: {
            fareAccommodation: currentQuote.pricing.accommodation,
            fareCleaning: currentQuote.pricing.cleaning,
            totalTaxes: currentQuote.pricing.taxes,
            subTotalPrice: currentQuote.pricing.accommodation + currentQuote.pricing.cleaning,
            currency: currentQuote.pricing.currency,
          },
        }),
      });

      setShowSuccess(true);
      setCheckIn(null);
      setCheckOut(null);
      setEstimatedPrice(null);
      setPricePerNight(null);
      setCurrentQuote(null);
      setQuoteExpired(false);
      setMinutesUntilExpiry(0);
      setIsEstimatedPrice(false);
      setNightlyRates(null);
    } catch (error) {
      console.error('Price fetch error:', error);
      setPriceError('Unable to add to cart. Please try again.');
    }
  }, [checkIn, checkOut, guests, unit.id, unit.name, unit.images, unit.locationId, unit.pricing.basePrice.currency, useGuestyAPI, guestyPriceVerified, currentQuote, isEstimatedPrice, nightlyRates, nightlyCleaningFee, estimatedPrice, addLodgeItem, nights]);

  /**
   * Remove from cart — delegates to UnifiedCartContext (single source of truth)
   */
  const handleRemoveItem = useCallback((cartItemId: string) => {
    removeItem(cartItemId);
  }, [removeItem]);

  /**
   * Proceed to checkout
   */
  const handleCheckout = useCallback(() => {
    router.push('/checkout');
  }, [router]);

  /**
   * Cleanup timer for success message
   */
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Close sheet automatically when dates are cleared
  useEffect(() => {
    if (!checkIn || !checkOut) {
      setShowSheet(false);
    }
  }, [checkIn, checkOut]);

  /** Auto-scroll to BookingCalendar on mount, perfectly centered.
   * Mobile-first: uses visualViewport so iOS/Android address bar doesn't
   * shift the center. Polls via RAF until calendar size stabilises.
   */
  const calendarPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    let aborted = false;
    let userScrolled = false;
    let lastTop = -1;
    let stableFrames = 0;
    let rafId = 0;
    const cancelOnGesture = () => { userScrolled = true; };
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === 'ArrowDown' || e.key === 'ArrowUp' ||
        e.key === 'PageDown' || e.key === 'PageUp' ||
        e.key === 'Home' || e.key === 'End' || e.key === ' '
      ) userScrolled = true;
    };
    window.addEventListener('wheel', cancelOnGesture, { passive: true });
    window.addEventListener('touchmove', cancelOnGesture, { passive: true });
    window.addEventListener('keydown', onKey);
    const computeTop = (): number | null => {
      const panel = calendarPanelRef.current;
      if (!panel) return null;
      const cal = panel.querySelector<HTMLElement>('[class*="BookingCalendar_container"]') || panel;
      const rect = cal.getBoundingClientRect();
      if (rect.height === 0) return null;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      if (rect.height <= vh) {
        return Math.max(0, window.scrollY + rect.top - (vh - rect.height) / 2);
      }
      return Math.max(0, window.scrollY + rect.top - 16);
    };
    const tick = () => {
      if (aborted || userScrolled) return;
      const top = computeTop();
      if (top === null) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      if (Math.abs(top - lastTop) > 1) {
        window.scrollTo({ top, behavior: 'auto' });
        lastTop = top;
        stableFrames = 0;
      } else {
        stableFrames++;
      }
      if (stableFrames < 30) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    const hardStop = setTimeout(() => { aborted = true; }, 3000);
    return () => {
      aborted = true;
      cancelAnimationFrame(rafId);
      clearTimeout(hardStop);
      window.removeEventListener('wheel', cancelOnGesture);
      window.removeEventListener('touchmove', cancelOnGesture);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // Can add to cart if dates selected, not loading, meets minimum nights,
  // and either has local pricing (estimatedPrice) or valid Guesty quote.
  // When GST active: price MUST be verified by Guesty API (defense-in-depth).
  const canAddToCart =
    checkIn &&
    checkOut &&
    !priceLoading &&
    !(useGuestyAPI && backgroundQuoteLoading) &&
    (!useGuestyAPI || guestyPriceVerified) &&
    meetsMinimumNights &&
    (estimatedPrice !== null || (currentQuote && !quoteExpired));

  const formatPrice = (amount: number) => {
    const currency = unit.pricing.basePrice.currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <section className={styles.bookingSection}>
      <div className={styles.container}>
        <div className={styles.bookingLayout}>
          {/* Left - Calendar */}
          <div ref={calendarPanelRef} className={styles.centerPanel}>
            <BookingCalendar
              itemType="lodge"
              itemId={unit.id}
              locale={calendarLocale}
              // Fallback nivel-3 ESTABLE del listing (nunca el valor reactivo
              // per-fecha: sería circular y contaminaría fechas sin dato).
              minimumNights={unit.pricing.minimumNights || 1}
              initialRange={initialRange}
              onRangeSelect={handleDateSelect}
              onEffectiveMinNightsChange={(n) => {
                setMinimumNightsRequired(n);
                onEffectiveMinNightsChange?.(n);
              }}
              guests={guests}
              showPrices={false}
              currency={(unit.pricing.basePrice.currency as 'USD' | 'CLP' | 'BRL' | 'EUR') || 'USD'}
            />

            {/* Mobile Price Card — appears directly below calendar when dates selected */}
            {checkIn && checkOut && (
              <div className={styles.mobilePriceCard}>
                {/* Dates row */}
                <div className={styles.mpcDates}>
                  <div className={styles.mpcDateBox}>
                    <span className={styles.mpcDateLabel}>{t('checkIn')}</span>
                    <span className={styles.mpcDateVal}>{format(checkIn, 'MMM d')}</span>
                    <span className={styles.mpcDateSub}>{format(checkIn, 'EEE')}</span>
                  </div>
                  <div className={styles.mpcDateDivider} />
                  <div className={styles.mpcDateBox}>
                    <span className={styles.mpcDateLabel}>{t('checkOut')}</span>
                    <span className={styles.mpcDateVal}>{format(checkOut, 'MMM d')}</span>
                    <span className={styles.mpcDateSub}>{format(checkOut, 'EEE')}</span>
                  </div>
                </div>

                {/* Price breakdown */}
                {priceLoading ? (
                  <div className={styles.mpcLoading}>
                    <span className={styles.spinner} />
                    <span>{t('calculatingPrice')}</span>
                  </div>
                ) : estimatedPrice ? (
                  <>
                    <div className={styles.mpcRows}>
                      {pricePerNight && (
                        <div className={styles.mpcRow}>
                          <span className={styles.mpcRowLabel}>
                            {backgroundQuoteLoading ? (
                              <><span className={styles.skeletonPulse} /> × {nights} {nights === 1 ? t('nightSingular') : t('nightPlural')}</>
                            ) : isCLP ? (
                              <><DualPrice amountCLP={pricePerNight} size="sm" showCurrencyCode={false} showCLP={false} locale="en" /> × {nights} {nights === 1 ? t('nightSingular') : t('nightPlural')}</>
                            ) : (
                              <>{formatPrice(pricePerNight)} × {nights} {nights === 1 ? t('nightSingular') : t('nightPlural')}</>
                            )}
                            {nights > 1 ? <> {t('avgMarker')}</> : ''}
                          </span>
                          <span className={styles.mpcRowVal}>
                            {backgroundQuoteLoading ? (
                              <span className={styles.skeletonPulseWide} />
                            ) : isCLP ? (
                              <DualPrice amountCLP={estimatedPrice - nightlyCleaningFee} size="sm" showCurrencyCode={false} showCLP={false} locale="en" />
                            ) : (
                              formatPrice(estimatedPrice - nightlyCleaningFee)
                            )}
                          </span>
                        </div>
                      )}
                      {nightlyCleaningFee > 0 && (
                        <div className={styles.mpcRow}>
                          <span className={styles.mpcRowLabel}>{t('cleaningFee')}</span>
                          <span className={styles.mpcRowVal}>
                            {backgroundQuoteLoading ? (
                              <span className={styles.skeletonPulse} />
                            ) : isCLP ? (
                              <DualPrice amountCLP={nightlyCleaningFee} size="sm" showCurrencyCode={false} showCLP={false} locale="en" />
                            ) : (
                              formatPrice(nightlyCleaningFee)
                            )}
                          </span>
                        </div>
                      )}
                      <div className={`${styles.mpcRow} ${styles.mpcRowTotal}`}>
                        <span className={styles.mpcRowLabel}>{t('total')}</span>
                        <span className={styles.mpcRowVal}>
                          {backgroundQuoteLoading ? (
                            <span className={styles.skeletonPulseWide} />
                          ) : isCLP && consistentUSDTotal && !rateLoading
                            ? `USD $${consistentUSDTotal.toFixed(2)}`
                            : formatPrice(estimatedPrice)
                          }
                        </span>
                      </div>
                    </div>

                    {/* Guests row */}
                    <div className={styles.mpcGuestRow}>
                      <span className={styles.mpcGuestLabel}>{t('guests')}</span>
                      <div className={styles.mpcGuestControls}>
                        <button
                          type="button"
                          className={styles.mpcGuestBtn}
                          onClick={() => handleGuestChange(guests - 1)}
                          disabled={guests <= 1}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14"/></svg>
                        </button>
                        <span className={styles.mpcGuestCount}>{guests}</span>
                        <button
                          type="button"
                          className={styles.mpcGuestBtn}
                          onClick={() => handleGuestChange(guests + 1)}
                          disabled={guests >= maxGuests}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
                        </button>
                      </div>
                      <span className={styles.mpcGuestMax}>{t('maxGuests', { count: maxGuests })}</span>
                    </div>

                    {/* Add to Cart */}
                    <button
                      type="button"
                      className={styles.mpcCta}
                      onClick={handleAddToCart}
                      disabled={!canAddToCart}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                      </svg>
                      {(useGuestyAPI && backgroundQuoteLoading)
                        ? t('verifyingPrice')
                        : canAddToCart
                          ? t('addToCart', { price: isCLP && consistentUSDTotal ? `USD $${consistentUSDTotal.toFixed(2)}` : formatPrice(estimatedPrice!) })
                          : priceLoading
                            ? t('calculating')
                            : t(minimumNights !== 1 ? 'minNightsPlural' : 'minNightsSingular', { count: minimumNights })
                      }
                    </button>
                  </>
                ) : priceError ? (
                  <div className={styles.mpcError}>{priceError}</div>
                ) : null}
              </div>
            )}

            {/* Activity Cross-Sell — accede directamente al estado de fechas del lodge */}
            <div className={styles.calendarCrossSell}>
              <ActivityCrossSell
                locale={calendarLocale}
                locationId={unit.locationId}
                lodgeCheckIn={checkIn ? format(checkIn, 'yyyy-MM-dd') : undefined}
                lodgeCheckOut={checkOut ? format(checkOut, 'yyyy-MM-dd') : undefined}
                guests={guests}
              />
              <ServicesCrossSell />
            </div>
          </div>

          {/* Right Panel */}
          <div className={styles.rightPanel}>
            <div className={styles.stickyContainer}>
              <div className={styles.dkCard}>
                {/* Date boxes */}
                {checkIn && checkOut ? (
                  <div className={styles.dkDates}>
                    <div className={styles.dkDateBox}>
                      <span className={styles.dkDateLabel}>{t('checkIn')}</span>
                      <span className={styles.dkDateVal}>{format(checkIn, 'MMM d')}</span>
                      <span className={styles.dkDateSub}>{format(checkIn, 'EEE')}</span>
                    </div>
                    <div className={styles.dkDateDivider} />
                    <div className={styles.dkDateBox}>
                      <span className={styles.dkDateLabel}>{t('checkOut')}</span>
                      <span className={styles.dkDateVal}>{format(checkOut, 'MMM d')}</span>
                      <span className={styles.dkDateSub}>{format(checkOut, 'EEE')}</span>
                    </div>
                  </div>
                ) : (
                  <div className={styles.dkPrompt}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {t('selectYourDates')}
                  </div>
                )}

                {/* Guest row */}
                <div className={styles.dkGuestRow}>
                  <span className={styles.dkGuestLabel}>{t('guests')}</span>
                  <div className={styles.dkGuestControls}>
                    <button
                      type="button"
                      className={styles.dkGuestBtn}
                      onClick={() => handleGuestChange(guests - 1)}
                      disabled={guests <= 1}
                      aria-label={t('decreaseGuests')}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14"/></svg>
                    </button>
                    <span className={styles.dkGuestCount}>{guests}</span>
                    <button
                      type="button"
                      className={styles.dkGuestBtn}
                      onClick={() => handleGuestChange(guests + 1)}
                      disabled={guests >= maxGuests}
                      aria-label={t('increaseGuests')}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
                    </button>
                  </div>
                  <span className={styles.dkGuestMax}>{t('maxGuests', { count: maxGuests })}</span>
                </div>

                {/* Minimum Nights */}
                {minimumNights > 1 && (
                  <div className={styles.dkMinNights}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                    <span>{t('minimumNights', { count: minimumNights })}</span>
                  </div>
                )}

                {/* Price breakdown */}
                {checkIn && checkOut && (
                  <>
                    {priceLoading ? (
                      <div className={styles.dkLoading}>
                        <span className={styles.spinner} />
                        <span>{t('calculatingPrice')}</span>
                      </div>
                    ) : estimatedPrice ? (
                      <>
                        <div className={styles.dkRows}>
                          {pricePerNight && (
                            <div className={styles.dkRow}>
                              <span className={styles.dkRowLabel}>
                                {backgroundQuoteLoading ? (
                                  <><span className={styles.skeletonPulse} /> × {nights} {nights === 1 ? t('nightSingular') : t('nightPlural')}</>
                                ) : isCLP ? (
                                  <><DualPrice amountCLP={pricePerNight} size="sm" showCurrencyCode={false} showCLP={false} locale="en" /> × {nights} {nights === 1 ? t('nightSingular') : t('nightPlural')}</>
                                ) : (
                                  <>{formatPrice(pricePerNight)} × {nights} {nights === 1 ? t('nightSingular') : t('nightPlural')}</>
                                )}
                                {nights > 1 ? <> {t('avgMarker')}</> : ''}
                              </span>
                              <span className={styles.dkRowVal}>
                                {backgroundQuoteLoading ? (
                                  <span className={styles.skeletonPulseWide} />
                                ) : isCLP ? (
                                  <DualPrice amountCLP={estimatedPrice - nightlyCleaningFee} size="sm" showCurrencyCode={false} showCLP={false} locale="en" />
                                ) : (
                                  formatPrice(estimatedPrice - nightlyCleaningFee)
                                )}
                              </span>
                            </div>
                          )}
                          {nightlyCleaningFee > 0 && (
                            <div className={styles.dkRow}>
                              <span className={styles.dkRowLabel}>{t('cleaningFee')}</span>
                              <span className={styles.dkRowVal}>
                                {backgroundQuoteLoading ? (
                                  <span className={styles.skeletonPulse} />
                                ) : isCLP ? (
                                  <DualPrice amountCLP={nightlyCleaningFee} size="sm" showCurrencyCode={false} showCLP={false} locale="en" />
                                ) : (
                                  formatPrice(nightlyCleaningFee)
                                )}
                              </span>
                            </div>
                          )}
                          <div className={`${styles.dkRow} ${styles.dkRowTotal}`}>
                            <span className={styles.dkRowLabel}>{t('total')}</span>
                            <span className={styles.dkRowVal}>
                              {backgroundQuoteLoading ? (
                                <span className={styles.skeletonPulseWide} />
                              ) : isCLP && consistentUSDTotal && !rateLoading
                                ? `USD $${consistentUSDTotal.toFixed(2)}`
                                : formatPrice(estimatedPrice)
                              }
                            </span>
                          </div>
                        </div>

                        {/* Quote indicators */}
                        {useGuestyAPI && currentQuote && !quoteExpired && (
                          <div className={styles.dkQuoteInfo}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 6v6l4 2" />
                            </svg>
                            <span>{t('priceGuaranteed', { minutes: minutesUntilExpiry })}</span>
                          </div>
                        )}
                        {useGuestyAPI && quoteExpired && (
                          <div className={styles.dkError}>
                            {t('priceExpired')}
                          </div>
                        )}
                      </>
                    ) : priceError ? (
                      <div className={styles.dkError}>{priceError}</div>
                    ) : null}
                  </>
                )}

                {/* CTA */}
                <button
                  type="button"
                  className={styles.dkCta}
                  onClick={handleAddToCart}
                  disabled={!canAddToCart}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                  {priceLoading ? (
                    t('calculating')
                  ) : (useGuestyAPI && backgroundQuoteLoading) ? (
                    t('verifyingPrice')
                  ) : canAddToCart && estimatedPrice ? (
                    t('addToCart', { price: isCLP && consistentUSDTotal ? `USD $${consistentUSDTotal.toFixed(2)}` : formatPrice(estimatedPrice) })
                  ) : checkIn && checkOut && !meetsMinimumNights ? (
                    t(minimumNights !== 1 ? 'minNightsPlural' : 'minNightsSingular', { count: minimumNights })
                  ) : (
                    t('selectDates')
                  )}
                </button>
              </div>

              {/* Success Message */}
              {showSuccess && (
                <div className={styles.successMessage}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <path d="M22 4 12 14.01l-3-3" />
                  </svg>
                  <span>{t('addedToCart')}</span>
                </div>
              )}

              {/* Service Recommendation */}
              {checkIn && checkOut && (
                <ServiceRecommendation
                  context="lodge"
                  prefillDates={{
                    checkIn: checkIn.toISOString().split('T')[0],
                    checkOut: checkOut.toISOString().split('T')[0],
                  }}
                  prefillGuests={guests}
                />
              )}

              {/* Cart */}
              <div className={styles.cartContainer}>
                <BookingCart
                  items={cartItemsFromContext}
                  locale="en"
                  currency={(unit.pricing.basePrice.currency as 'USD' | 'CLP' | 'BRL' | 'EUR') || 'USD'}
                  onRemoveItem={handleRemoveItem}
                  onCheckout={handleCheckout}
                  checkoutEnabled={cartItemsFromContext.length > 0}
                  compact={true}
                  hideSummary={true}
                />
              </div>

              {/* Custom Summary — handles mixed currencies correctly */}
              {cartItemsFromContext.length > 0 && (
                <div className={styles.cartSummary}>
                  <h4 className={styles.cartSummaryTitle}>{t('summary')}</h4>

                  {/* Lodge subtotal in CLP → shown as USD equivalent */}
                  {lodgeItems.length > 0 && (
                    <div className={styles.cartSummaryRow}>
                      <span>{t('lodge')}</span>
                      <span>
                        <DualPrice
                          amountCLP={lodgeTotalCLP}
                          size="sm"
                          showCurrencyCode={true}
                          showCLP={false}
                          locale={calendarLocale}
                        />
                      </span>
                    </div>
                  )}

                  {/* Activity subtotal in USD */}
                  {activityItems.length > 0 && (
                    <div className={styles.cartSummaryRow}>
                      <span>{t('activitiesLabel')}</span>
                      <span>${activitySubtotalUSD.toFixed(0)}</span>
                    </div>
                  )}

                  {/* Service fee (if applicable) */}
                  {activityServiceFee > 0 && (
                    <div className={styles.cartSummaryRow}>
                      <span>{t('serviceFee')}</span>
                      <span>${activityServiceFee.toFixed(0)}</span>
                    </div>
                  )}

                  {/* Coupon discount */}
                  {couponDiscount > 0 && (
                    <div className={styles.cartSummaryRow}>
                      <span>{t('discount')}</span>
                      <span className={styles.cartSummaryDiscount}>-${couponDiscount.toFixed(0)}</span>
                    </div>
                  )}

                  {/* Grand total in USD */}
                  <div className={styles.cartSummaryTotal}>
                    <span>{t('total')}</span>
                    <span className={styles.cartSummaryTotalAmount}>USD ${grandTotalUSD.toFixed(2)}</span>
                  </div>

                  {/* Exchange rate info */}
                  {lodgeItems.length > 0 && cartExchangeRate && (
                    <div className={styles.cartSummaryRate}>
                      1 USD ≈ {cartExchangeRate.toFixed(0)} CLP
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Bar — only show when no dates yet (call to action) */}
      {!checkIn && (
        <div className={styles.mobileBottomBar}>
          <span className={styles.mobilePriceLabel}>{t('selectDatesToSeePricing')}</span>
        </div>
      )}
    </section>
  );
}
